/**
 * Pins that are events: a name and a day, maybe a time, and whether the
 * family is reminded on the morning of it. What makes a pin say what is
 * coming up rather than what somebody once thought worth keeping.
 *
 * Days are 'yyyy-mm-dd' in the family's zone (dayKey), compared as strings,
 * and counted between at noon UTC so no zone's midnight can move them.
 */

export interface PinEvent {
  name: string;
  /** 'yyyy-mm-dd'. */
  day: string;
  /** 'hh:mm', 24-hour, when there is one. */
  time?: string;
  /** Tell everyone at 8 that morning. */
  remind?: boolean;
}

export const EVENT_NAME_MAX = 60;

const DAY_MS = 86_400_000;

function noonOf(day: string): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d, 12);
}

function keyOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function addDays(day: string, n: number): string {
  return keyOf(noonOf(day) + n * DAY_MS);
}

export function daysUntil(day: string, today: string): number {
  return Math.round((noonOf(day) - noonOf(today)) / DAY_MS);
}

export function isPast(event: Pick<PinEvent, 'day'>, today: string): boolean {
  return event.day < today;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2 PM", "2:30 PM", "Noon". */
export function clockWords(time: string): string {
  const [h, m] = time.split(':').map(Number) as [number, number];
  if (h === 12 && m === 0) return 'Noon';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
}

/** "today", "tomorrow", "in 3 days", "in 2 weeks". */
export function relativeWords(day: string, today: string): string {
  const n = daysUntil(day, today);
  if (n <= 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n < 14) return `in ${n} days`;
  return `in ${Math.round(n / 7)} weeks`;
}

/** The calendar tile: 'OCT' over '18'. */
export function tileOf(day: string): { month: string; date: string } {
  const at = new Date(noonOf(day));
  return { month: MONTHS[at.getUTCMonth()]!.toUpperCase(), date: String(at.getUTCDate()) };
}

/** "Sun · 2 PM · in 3 days", or "Sun · today". */
export function whenWords(event: Pick<PinEvent, 'day' | 'time'>, today: string): string {
  const weekday = WEEKDAYS[new Date(noonOf(event.day)).getUTCDay()]!;
  return [weekday, event.time ? clockWords(event.time) : null, relativeWords(event.day, today)]
    .filter(Boolean)
    .join(' · ');
}

/** "Sun, Oct 18". */
export function dayWords(day: string): string {
  const at = new Date(noonOf(day));
  return `${WEEKDAYS[at.getUTCDay()]}, ${MONTHS[at.getUTCMonth()]} ${at.getUTCDate()}`;
}

const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function valid(y: number, m: number, d: number): string | null {
  const at = new Date(Date.UTC(y, m - 1, d, 12));
  if (at.getUTCMonth() !== m - 1 || at.getUTCDate() !== d) return null;
  return keyOf(at.getTime());
}

/** This year's, or next year's if this year's has been and gone. */
function nextOf(month: number, date: number, today: string): string | null {
  const year = Number(today.slice(0, 4));
  const now = valid(year, month, date);
  if (now && now >= today) return now;
  return valid(year + 1, month, date);
}

/**
 * The day a message is talking about, if it names one: "Oct 25", "October
 * 25th", "10/25", "Sunday the 18th", "the 18th", "Saturday", "tomorrow".
 * The most particular wins. Never a day already past.
 */
export function findDate(text: string, today: string): string | null {
  const said = text.toLowerCase();

  const named = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?\b/.exec(said);
  if (named) {
    const month = MONTH_NAMES.indexOf(named[1]!.slice(0, 3)) + 1;
    const found = nextOf(month, Number(named[2]), today);
    if (found) return found;
  }

  const numeric = /\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?\b/.exec(said);
  if (numeric) {
    const [m, d] = [Number(numeric[1]), Number(numeric[2])];
    if (numeric[3]) {
      const y = Number(numeric[3].length === 2 ? `20${numeric[3]}` : numeric[3]);
      const found = valid(y, m, d);
      if (found && found >= today) return found;
    } else {
      const found = nextOf(m, d, today);
      if (found) return found;
    }
  }

  const ordinal = /\bthe (\d{1,2})(?:st|nd|rd|th)\b/.exec(said);
  if (ordinal) {
    const d = Number(ordinal[1]);
    const [y, m] = today.split('-').map(Number) as [number, number];
    const thisMonth = valid(y, m, d);
    if (thisMonth && thisMonth >= today) return thisMonth;
    const next = m === 12 ? valid(y + 1, 1, d) : valid(y, m + 1, d);
    if (next) return next;
  }

  const weekday = new RegExp(`\\b(${WEEKDAY_NAMES.join('|')})\\b`).exec(said);
  if (weekday) {
    const want = WEEKDAY_NAMES.indexOf(weekday[1]!);
    const have = new Date(noonOf(today)).getUTCDay();
    // The coming one: a Sunday named on a Sunday is next week's.
    return addDays(today, ((want - have + 7) % 7) || 7);
  }

  if (/\btomorrow\b/.test(said)) return addDays(today, 1);
  if (/\b(today|tonight)\b/.test(said)) return today;
  return null;
}

/** The time a message names, if it says am or pm, or noon: 'hh:mm'. */
export function findTime(text: string): string | null {
  const said = text.toLowerCase();
  if (/\bnoon\b/.test(said)) return '12:00';
  const clock = /\b(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)(?![a-z])/.exec(said);
  if (!clock) return null;
  let hour = Number(clock[1]);
  const minute = Number(clock[2] ?? 0);
  if (hour < 1 || hour > 12 || minute > 59) return null;
  const pm = clock[3]!.startsWith('p');
  if (pm && hour !== 12) hour += 12;
  if (!pm && hour === 12) hour = 0;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** A name to start from: the message's first line, cut at a word. */
export function suggestName(text: string | undefined): string {
  const line = (text ?? '').trim().split('\n')[0] ?? '';
  if (line.length <= 40) return line;
  const cut = line.slice(0, 40);
  return `${cut.slice(0, cut.lastIndexOf(' ') > 20 ? cut.lastIndexOf(' ') : 40).trim()}…`;
}

/** When a pin went up; one still being written counts as just now. */
function pinnedAt(at: unknown): number {
  return (at as { toMillis?: () => number } | null)?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
}

/**
 * The order pins are shown in: events still to come, soonest first and by
 * time on the same day, then plain pins, newest first. Events that have been
 * and gone are left out.
 */
export function orderPins<T extends { event?: PinEvent; at?: unknown }>(pins: readonly T[], today: string): T[] {
  const events = pins
    .filter((pin) => pin.event && !isPast(pin.event, today))
    .sort(
      (a, b) =>
        a.event!.day.localeCompare(b.event!.day) ||
        (a.event!.time ?? '99:99').localeCompare(b.event!.time ?? '99:99'),
    );
  const plain = pins.filter((pin) => !pin.event).sort((a, b) => pinnedAt(b.at) - pinnedAt(a.at));
  return [...events, ...plain];
}

/**
 * The morning's work: which events to remind the family of, and which have
 * been and gone and come down.
 */
export function eventChores<T extends { event?: PinEvent }>(pins: readonly T[], today: string): { remind: T[]; expired: T[] } {
  return {
    remind: pins.filter((pin) => pin.event?.remind && pin.event.day === today),
    expired: pins.filter((pin) => pin.event && isPast(pin.event, today)),
  };
}

/** What the reminder says under "Today: <name>". */
export function reminderBody(event: Pick<PinEvent, 'time'>): string {
  return event.time ? `${clockWords(event.time)} · tap to see the message` : 'Tap to see the message';
}
